import React, { useRef, useState, useEffect } from "react";
import { MapPin, X } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectLocationForm = ({
  placeholderData,
  setPlaceholderData,
  onSave,
  setActiveTab,
  isProject,
}) => {
  const cardCls = "bg-white rounded-xl shadow p-8 mb-10";
  const labelCls = "block text-sm font-medium text-gray-700 mb-2";
  const inputCls =
    "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

  const skipAutosave = useRef(false);
  const [draftId, setDraftId] = useState(null);

  const saveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const loadDraft = async () => {
      const storedDraftId = localStorage.getItem("draftId");
      if (!storedDraftId) {
        isInitialLoad.current = false;
        return;
      }

      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `${API_BASE_URL}/admin/get-draft-project/${storedDraftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.draft) {
          setPlaceholderData(res.data.draft.draft_data);
          setDraftId(res.data.draft.draft_id);
        }
      } catch (err) {
        console.error("Error loading draft:", err);
      } finally {
        isInitialLoad.current = false;
      }
    };

    if (!isProject) loadDraft();
    else isInitialLoad.current = false;
  }, [isProject, API_BASE_URL, setPlaceholderData]);

  const autosaveDraft = async () => {
    if (!placeholderData.project_name?.trim()) return;

    const token = localStorage.getItem("token");
    const payload = {
      draft_id: draftId,
      formData: placeholderData,
    };

    try {
      const res = await axios.post(
        `${API_BASE_URL}/admin/save-draft-project`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!draftId && res.data.draft_id) {
        setDraftId(res.data.draft_id);
        localStorage.setItem("draftId", res.data.draft_id);
      }
    } catch (err) {
      console.error("Autosave failed:", err);
    }
  };

  useEffect(() => {
    if (!placeholderData || !placeholderData.project_name?.trim()) return;

    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      autosaveDraft();
    }, 800);

    return () => clearTimeout(saveTimer.current);
  }, [placeholderData]);

  const clearForm = () => {
    skipAutosave.current = true;
    setPlaceholderData({
      ...placeholderData,
      project_location: "",
    });
    localStorage.setItem("draftId", null);
  };

  console.log("Rendering ProjectLocationForm with data:", placeholderData);

  return (
    <section className={cardCls}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Project Location
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={clearForm}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
          >
            <X size={18} /> Clear
          </button>

          <button
            onClick={() => setActiveTab("amenities")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Next
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className={labelCls}>Project Location URL</label>
          <input
            type="text"
            className={inputCls}
            placeholder="Paste Google Maps location URL here"
            value={placeholderData?.project_location || ""}
            onChange={(e) =>
              setPlaceholderData((prev) => ({
                ...prev,
                project_location: e.target.value,
              }))
            }
          />
        </div>

        <div className="bg-blue-50 p-4 rounded-lg mt-4">
          <p className="text-sm text-blue-800 flex items-center gap-2">
            <MapPin size={18} className="text-blue-600" />
            <span>
              <strong>Note:</strong> Paste the exact map/location URL you want
              to show on the project detail page.
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProjectLocationForm;
